import React from "react";
import MapRouteSidebar from "./MapRouteSidebar";
import { SurfacesVisibility } from "../contexts/DataContext";

// Reusable visibility toggle button component
const VisibilityToggleButton = ({
  indent,
  isVisible,
  onToggle,
  label,
}: {
  indent?: boolean;
  isVisible: boolean;
  onToggle: () => void;
  label: string;
}) => {
  const color = isVisible ? "text-black" : "text-gray-400";
  const marginLeft = indent ? "ml-4" : "";

  return (
    <button
      onClick={onToggle}
      className={`font-medium text-left text-sm h-4 mb-3 ${color} ${marginLeft}`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 576 512"
        className="h-3 w-3 inline-block mr-2"
      >
        <path
          d="M288 32c-80.8 0-145.5 36.8-192.6 80.6-46.8 43.5-78.1 95.4-93 131.1-3.3 7.9-3.3 16.7 0 24.6 14.9 35.7 46.2 87.7 93 131.1 47.1 43.7 111.8 80.6 192.6 80.6s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1 3.3-7.9 3.3-16.7 0-24.6-14.9-35.7-46.2-87.7-93-131.1-47.1-43.7-111.8-80.6-192.6-80.6zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64-11.5 0-22.3-3-31.7-8.4-1 10.9-.1 22.1 2.9 33.2 13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-12.2-45.7-55.5-74.8-101.1-70.8 5.3 9.3 8.4 20.1 8.4 31.7z"
          fill="currentColor"
        />
      </svg>
      {label}
    </button>
  );
};

// Map controls sidebar component
export default function ExportSidebar({
  surfacesVisibility,
  setSurfacesVisibility,
}: {
  surfacesVisibility: SurfacesVisibility;
  setSurfacesVisibility: React.Dispatch<
    React.SetStateAction<SurfacesVisibility>
  >;
}) {
  const allVisible = Object.values(surfacesVisibility).every(Boolean);

  return (
    <MapRouteSidebar title="Configure Export">
      <div style={{ display: "flex", flexDirection: "column" }}>
        <VisibilityToggleButton
          isVisible={allVisible}
          onToggle={() =>
            setSurfacesVisibility({
              surfaceFeatures: !allVisible ? true : false,
              hazardMarkers: !allVisible ? true : false,
              labelMarkers: !allVisible ? true : false,
              arrows: !allVisible ? true : false,
              boundary: !allVisible ? true : false,
              features: surfacesVisibility.features.map((f) => ({
                ...f,
                visible: !allVisible ? true : false,
              })),
            })
          }
          label="Show All"
        />
        <VisibilityToggleButton
          isVisible={surfacesVisibility.surfaceFeatures}
          onToggle={() =>
            setSurfacesVisibility((prev) => ({
              ...prev,
              surfaceFeatures: !prev.surfaceFeatures,
              features: prev.features.map((f) => ({
                ...f,
                visible: !prev.surfaceFeatures,
              })),
            }))
          }
          label="Surface Features"
        />
        {surfacesVisibility.features?.map((featureType) => (
          <VisibilityToggleButton
            key={featureType.label}
            indent={true}
            isVisible={featureType.visible}
            onToggle={() => {
              setSurfacesVisibility((prev) => {
                return {
                  ...prev,
                  surfaceFeatures: prev.features.reduce(
                    (allVisible, f) =>
                      f.value === featureType.value
                        ? allVisible && !f.visible
                        : allVisible && f.visible,
                    true,
                  ),
                  features: prev.features.map((f) =>
                    f.value === featureType.value
                      ? { ...f, visible: !f.visible }
                      : f,
                  ),
                };
              });
            }}
            label={featureType.label}
          />
        ))}
        <VisibilityToggleButton
          isVisible={surfacesVisibility.hazardMarkers}
          onToggle={() =>
            setSurfacesVisibility((prev) => ({
              ...prev,
              hazardMarkers: !prev.hazardMarkers,
            }))
          }
          label="Hazard Markers"
        />
        <VisibilityToggleButton
          isVisible={surfacesVisibility.labelMarkers}
          onToggle={() =>
            setSurfacesVisibility((prev) => ({
              ...prev,
              labelMarkers: !prev.labelMarkers,
            }))
          }
          label="Label Markers"
        />
        <VisibilityToggleButton
          isVisible={surfacesVisibility.arrows}
          onToggle={() =>
            setSurfacesVisibility((prev) => ({
              ...prev,
              arrows: !prev.arrows,
            }))
          }
          label="Arrows"
        />
        <VisibilityToggleButton
          isVisible={surfacesVisibility.boundary}
          onToggle={() =>
            setSurfacesVisibility((prev) => ({
              ...prev,
              boundary: !prev.boundary,
            }))
          }
          label="Property Boundary"
        />
      </div>
    </MapRouteSidebar>
  );
}
